import { api } from './api';
import { Movimento, ScadenzeOverview } from '@/types';

/**
 * Backend response format for movimento
 */
interface MovimentoBackend {
  id: number;
  tipo: 'ENTRATA' | 'USCITA';
  descrizione?: string;
  importo: number;
  data?: string;
  dataMovimento?: string;
  note?: string;
  categoriaId: number;
  categoriaNome?: string;
  risorsaId?: number;
  risorsaNome?: string;
  clienteId?: number;
  clienteNome?: string;
  articoloId?: number;
  articoloNome?: string;
  tipoPagamento?: string;
  quantita?: number;
  isPagato?: boolean;
  numeroOrdine?: string;
  comune?: string;
  provincia?: string;
  createdAt?: string;
  updatedAt?: string;
  numeroRata?: number;
  totaleRate?: number;
  gruppoRateId?: string;
  dataScadenza?: string;
  frequenzaRate?: string;
}

/**
 * Transform backend response to frontend Movimento type
 */
const transformToMovimento = (response: MovimentoBackend): Movimento => {
  return {
    id: response.id,
    tipo: response.tipo.toLowerCase() as 'entrata' | 'uscita',
    importo: response.importo,
    data: response.data || response.dataMovimento || '',
    descrizione: response.descrizione || response.note || '',
    categoria_id: response.categoriaId,
    categoria: response.categoriaNome ? { id: response.categoriaId, nome: response.categoriaNome, tipo: 'entrambi', attiva: true, utente_id: 0 } : undefined,
    risorsa_id: response.risorsaId,
    risorsa: response.risorsaNome ? { id: response.risorsaId!, nome: response.risorsaNome, tipo: 'altro', saldo_iniziale: 0, saldo_corrente: 0, attiva: true, utente_id: 0 } : undefined,
    rubrica_id: response.clienteId,
    contatto: response.clienteNome ? { id: response.clienteId!, nome: response.clienteNome, utente_id: 0 } : undefined,
    articolo_id: response.articoloId,
    tipoPagamento: response.tipoPagamento,
    quantita: response.quantita,
    note: response.note,
    isPagato: response.isPagato,
    numeroOrdine: response.numeroOrdine,
    comune: response.comune,
    provincia: response.provincia,
    utente_id: 0,
    data_creazione: response.createdAt || response.data || '',
    data_modifica: response.updatedAt,
    numeroRata: response.numeroRata,
    totaleRate: response.totaleRate,
    gruppoRateId: response.gruppoRateId,
    dataScadenza: response.dataScadenza,
    frequenzaRate: response.frequenzaRate,
  };
};

/**
 * Get scadenze overview (counts and totals)
 */
const getOverview = async (): Promise<ScadenzeOverview> => {
  const response = await api.get<ScadenzeOverview>('/scadenze/overview');
  return response.data;
};

/**
 * Get overdue payments (scadute)
 */
const getScadute = async (): Promise<Movimento[]> => {
  const response = await api.get<MovimentoBackend[]>('/scadenze/scadute');
  return response.data.map(transformToMovimento);
};

/**
 * Get upcoming payments (prossime) within specified days
 */
const getProssime = async (giorni = 30): Promise<Movimento[]> => {
  const response = await api.get<MovimentoBackend[]>(`/scadenze/prossime?giorni=${giorni}`);
  return response.data.map(transformToMovimento);
};

/**
 * Get scheduled/future payments (programmate)
 */
const getProgrammate = async (): Promise<Movimento[]> => {
  const response = await api.get<MovimentoBackend[]>('/scadenze/programmate');
  return response.data.map(transformToMovimento);
};

/**
 * Mark a payment as paid
 */
const markAsPaid = async (id: number): Promise<Movimento> => {
  const response = await api.post<MovimentoBackend>(`/scadenze/${id}/paga`);
  return transformToMovimento(response.data);
};

const scadenzeService = {
  getOverview,
  getScadute,
  getProssime,
  getProgrammate,
  markAsPaid,
};

export default scadenzeService;
